import React, { useState, useEffect, useMemo } from 'react';
import {
    MdSearch, MdAccessTime, MdLocalOffer, MdContentCut,
    MdKeyboardArrowDown, MdKeyboardArrowUp, MdPerson,
    MdWarning, MdKeyboardArrowLeft, MdKeyboardArrowRight,
    MdSpa
} from 'react-icons/md';

import { servicesAPI } from 'services/api';

// --- YARDIMCI FONKSİYONLAR ---
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 0,
    }).format(amount);
};

// Gelişmiş Türkçe Karakter Normalizasyonu ("sac" == "SAÇ" eşleşmesi için)
const normalizeString = (str) => {
    if (!str) return '';
    return str.toLocaleLowerCase('tr-TR')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c');
};

// --- SKELETON LOADER ---
const PriceListSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
        {[...Array(6)].map((_, i) => (
            <div key={i} className="flex flex-col h-[280px] w-full rounded-[20px] bg-gray-200 dark:bg-navy-700 p-6">
                <div className="flex justify-between mb-4">
                    <div className="h-6 w-1/2 bg-gray-300 dark:bg-navy-600 rounded" />
                    <div className="h-8 w-20 bg-gray-300 dark:bg-navy-600 rounded-xl" />
                </div>
                <div className="flex gap-2 mb-4">
                    <div className="h-6 w-20 bg-gray-300 dark:bg-navy-600 rounded-full" />
                    <div className="h-6 w-20 bg-gray-300 dark:bg-navy-600 rounded-full" />
                </div>
                <div className="h-4 w-full bg-gray-300 dark:bg-navy-600 rounded mb-2" />
                <div className="h-4 w-4/5 bg-gray-300 dark:bg-navy-600 rounded mb-4" />
                <div className="mt-auto h-10 w-full bg-gray-300 dark:bg-navy-600 rounded-lg" />
            </div>
        ))}
    </div>
);

const CustomerPriceList = () => {
    // --- STATELER (WORK-LOGIC BİREBİR) ---
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filtre Stateleri
    const [searchTerm, setSearchTerm] = useState('');
    const [genderFilter, setGenderFilter] = useState('all'); // 'all', 'male', 'female'

    // Genişleyen (Accordion) Kart State'i
    const [expandedId, setExpandedId] = useState(null);

    // Sayfalama (Pagination)
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 9;

    // --- API FETCH ---
    useEffect(() => {
        const fetchServices = async () => {
            try {
                setLoading(true);
                const response = await servicesAPI.getAll();

                if (response && response.services) {
                    // İsme göre alfabetik sıralama
                    const sortedServices = response.services.sort((a, b) =>
                        a.name.localeCompare(b.name, 'tr-TR')
                    );
                    setServices(sortedServices);
                } else {
                    setServices([]);
                }
            } catch (err) {
                console.error("Hizmetler yüklenirken hata:", err);
                setError("Hizmet listesi alınırken bir sorun oluştu.");
            } finally {
                setLoading(false);
            }
        };

        fetchServices();
    }, []);

    const toggleExpand = (id) => {
        setExpandedId(prevId => prevId === id ? null : id);
    };

    // --- FİLTRELEME İŞLEMLERİ (Gelişmiş Arama) ---
    const filteredServices = useMemo(() => {
        return services.filter((service) => {
            // 1. İsim / Açıklama Arama (Türkçe Karakter Uyumlu)
            const lowerSearch = normalizeString(searchTerm);
            const matchSearch = normalizeString(service.name).includes(lowerSearch) ||
                (service.description && normalizeString(service.description).includes(lowerSearch));

            // 2. Cinsiyet Filtresi
            let matchGender = true;
            if (genderFilter === 'male') {
                matchGender = service.target_gender === 'male' || service.target_gender === 'unisex' || !service.target_gender;
            } else if (genderFilter === 'female') {
                matchGender = service.target_gender === 'female' || service.target_gender === 'unisex' || !service.target_gender;
            }

            return matchSearch && matchGender;
        });
    }, [services, searchTerm, genderFilter]);

    // Filtre değişince sayfayı 1'e al
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, genderFilter]);

    // --- SAYFALAMA HESAPLARI ---
    const totalPages = Math.max(1, Math.ceil(filteredServices.length / PAGE_SIZE));
    const currentServices = filteredServices.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BAŞLIK VE FİLTRE ALANI */}
            <div className="flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden">
                {/* Dekoratif Header */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 md:p-8 relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 opacity-10">
                        <MdSpa className="text-[200px] text-white" />
                    </div>
                    <div className="relative z-10 text-white">
                        <h2 className="text-2xl md:text-3xl font-black mb-2 drop-shadow-sm">Hizmetler ve Fiyat Listesi</h2>
                        <p className="text-sm font-medium opacity-90">Size sunduğumuz ayrıcalıklı hizmetlerin güncel fiyatlarını inceleyin.</p>
                    </div>
                </div>

                {/* Arama ve Cinsiyet Filtresi */}
                <div className="p-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">

                        {/* Arama Çubuğu */}
                        <div className="relative w-full md:w-96">
                            <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                            <input
                                type="text"
                                placeholder="Hizmet ara (Örn: Saç Kesimi)..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-12 pl-11 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm font-bold text-navy-700 outline-none focus:border-green-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all shadow-sm"
                            />
                        </div>

                        {/* Segmented Control (Cinsiyet Filtresi) */}
                        <div className="flex p-1 bg-gray-100 dark:bg-navy-900 rounded-xl w-full md:w-auto shadow-inner">
                            <button
                                onClick={() => setGenderFilter('all')}
                                className={`flex-1 md:flex-none px-6 py-2 text-sm font-bold rounded-lg transition-all ${genderFilter === 'all' ? 'bg-white dark:bg-navy-800 text-green-500 shadow-sm' : 'text-gray-500 hover:text-navy-700 dark:hover:text-white'}`}
                            >
                                Tümü
                            </button>
                            <button
                                onClick={() => setGenderFilter('female')}
                                className={`flex-1 md:flex-none px-6 py-2 text-sm font-bold rounded-lg transition-all ${genderFilter === 'female' ? 'bg-white dark:bg-navy-800 text-green-500 shadow-sm' : 'text-gray-500 hover:text-navy-700 dark:hover:text-white'}`}
                            >
                                Kadın
                            </button>
                            <button
                                onClick={() => setGenderFilter('male')}
                                className={`flex-1 md:flex-none px-6 py-2 text-sm font-bold rounded-lg transition-all ${genderFilter === 'male' ? 'bg-white dark:bg-navy-800 text-green-500 shadow-sm' : 'text-gray-500 hover:text-navy-700 dark:hover:text-white'}`}
                            >
                                Erkek
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-sm font-bold flex items-center gap-2">
                    <MdWarning className="text-xl" /> {error}
                </div>
            )}

            {/* 2. HİZMET KARTLARI (GRID) */}
            <div>
                {loading ? (
                    <PriceListSkeleton />
                ) : filteredServices.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">
                        <MdContentCut className="text-6xl text-gray-300 dark:text-gray-600 mb-4 opacity-50" />
                        <h5 className="text-xl font-bold text-navy-700 dark:text-white">Hizmet Bulunamadı</h5>
                        <p className="text-sm font-medium text-gray-500 mt-2">Aradığınız kriterlere uygun bir işlem bulunmuyor.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {currentServices.map((service) => {
                                const isExpanded = expandedId === service.id;
                                // Bu hizmet için özel fiyatı olan personelleri bul (WORK-LOGIC BİREBİR KORUNDU)
                                const staffWithCustomPrices = service.staff?.filter(u => u.pivot?.price || u.pivot?.duration) || [];
                                const hasCustomPrices = staffWithCustomPrices.length > 0;

                                return (
                                    <div
                                        key={service.id}
                                        className={`flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 transition-all duration-300 overflow-hidden hover:shadow-md ${isExpanded ? 'ring-2 ring-green-500/50' : ''}`}
                                    >
                                        <div className="p-6 flex flex-col flex-1">

                                            {/* Başlık ve Ana Fiyat */}
                                            <div className="flex justify-between items-start mb-3 gap-2">
                                                <h5 className="text-lg font-bold text-navy-700 dark:text-white leading-tight">
                                                    {service.name}
                                                </h5>
                                                <div className="flex-none bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 px-3 py-1.5 rounded-xl border border-green-100 dark:border-green-500/20">
                                                    <span className="text-base font-black">{formatCurrency(service.price)}</span>
                                                </div>
                                            </div>

                                            {/* Rozetler (Süre & Tür) */}
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                {service.optional ? (
                                                    <span className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-500 dark:bg-blue-500/10 dark:text-blue-400 rounded-md text-[10px] font-bold uppercase tracking-wider">
                            <MdLocalOffer /> Opsiyonel
                          </span>
                                                ) : (
                                                    <span className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300 rounded-md text-[10px] font-bold uppercase tracking-wider">
                            <MdContentCut /> Ana Hizmet
                          </span>
                                                )}
                                                <span className="flex items-center gap-1 px-2.5 py-1 bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 rounded-md text-[10px] font-bold uppercase tracking-wider">
                          <MdAccessTime /> {service.duration} dk.
                        </span>
                                            </div>

                                            {/* Açıklama */}
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-6 flex-1 line-clamp-3">
                                                {service.description || "Bu hizmet için detaylı açıklama bulunmamaktadır."}
                                            </p>

                                            {/* PERSONELE ÖZEL FİYATLAR ALANI */}
                                            {hasCustomPrices && (
                                                <div className="mt-auto">
                                                    {/* Accordion Tetikleyici */}
                                                    <button
                                                        onClick={() => toggleExpand(service.id)}
                                                        className="flex items-center justify-between w-full p-3 rounded-xl bg-green-50 dark:bg-green-500/10 hover:bg-green-100 dark:hover:bg-green-500/20 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                                                            <MdPerson className="text-lg" />
                                                            <span className="text-xs font-bold uppercase tracking-wide">
                                {staffWithCustomPrices.length} Personele Özel Fiyat
                              </span>
                                                        </div>
                                                        <div className="text-green-600 dark:text-green-400 text-xl">
                                                            {isExpanded ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                                                        </div>
                                                    </button>

                                                    {/* Accordion İçerik (Animasyonlu Dropdown) */}
                                                    <div className={`transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                                                        <div className="flex flex-col gap-2 p-3 bg-gray-50 dark:bg-navy-900 rounded-xl border border-gray-100 dark:border-white/5">
                                                            {staffWithCustomPrices.map((staff, sIndex) => (
                                                                <div key={staff.id} className={`flex items-center justify-between py-2 ${sIndex !== staffWithCustomPrices.length - 1 ? 'border-b border-gray-200 dark:border-white/10' : ''}`}>
                                  <span className="text-sm font-bold text-navy-700 dark:text-white truncate pr-2">
                                    {staff.name} {staff.surname}
                                  </span>
                                                                    <div className="flex items-center gap-3 flex-none">
                                    <span className="text-[10px] font-bold text-gray-400">
                                      {staff.pivot.duration || service.duration} dk
                                    </span>
                                                                        <span className="text-sm font-black text-green-500">
                                      {formatCurrency(staff.pivot.price || service.price)}
                                    </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                        {/* Alt Çizgi Vurgusu */}
                                        <div className="h-1 w-full bg-gradient-to-r from-green-400 to-green-600" />
                                    </div>
                                );
                            })}
                        </div>

                        {/* 3. SAYFALAMA (PAGINATION) */}
                        {totalPages > 1 && (
                            <div className="flex flex-col sm:flex-row items-center justify-between mt-8 pt-6 border-t border-gray-100 dark:border-white/10 gap-4">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    <span className="font-bold text-navy-700 dark:text-white">{filteredServices.length}</span> hizmetten <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, filteredServices.length)}</span> gösteriliyor.
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                                    >
                                        <MdKeyboardArrowLeft className="text-2xl" />
                                    </button>
                                    <div className="flex h-10 min-w-[40px] items-center justify-center rounded-xl bg-green-500 text-white text-sm font-bold shadow-md px-3">
                                        {currentPage}
                                    </div>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                                    >
                                        <MdKeyboardArrowRight className="text-2xl" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

        </div>
    );
};

export default CustomerPriceList;